/**
 * ТЗ 0120 — общая модель строки фильтров и сохранённых видов реестра
 * («Лиды», «Договоры», …): чистые помощники без React, Apollo и i18n,
 * параметризованные списком ключей фильтра. Обёртка реестра (например
 * `Crm/leadsViewsModel`) подставляет свои ключи, пустые значения и пресеты.
 */

/** Значение фильтра задано: массив — непустой, иначе — истинное. */
export const filterValueSet = (value: unknown): boolean =>
  Array.isArray(value) ? value.length > 0 : Boolean(value);

/**
 * Активные фильтры поповера «Фильтры (N)» — по порядку `keys`; их число —
 * на кнопке. `isActive` — особые правила реестра (по умолчанию «задано»).
 */
export const activeHiddenFilters = <F, K extends keyof F>(
  keys: ReadonlyArray<K>,
  filters: F,
  isActive: (filters: F, key: K) => boolean = (value, key) =>
    filterValueSet(value[key]),
): K[] => keys.filter((key) => isActive(filters, key));

/** Снять один фильтр поповера (крошка ✕): вернуть его пустое значение. */
export const clearHiddenFilter = <F, K extends keyof F>(
  empty: F,
  key: K,
): Partial<F> => (({ [key]: empty[key] } as unknown) as Partial<F>);

/** Фильтры → JSON вида: только непустые ключи белого списка. */
export const filtersToViewJson = <F, K extends keyof F>(
  keys: ReadonlyArray<K>,
  filters: F,
): Partial<Pick<F, K>> => {
  const out: Partial<Pick<F, K>> = {};
  keys.forEach((key) => {
    const value = filters[key];
    if (filterValueSet(value)) out[key] = value;
  });
  return out;
};

/** JSON вида (чужая запись, старая версия) → запись; мусор — пустая. */
export const viewJsonRecord = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};

/** Строки из JSON вида: всё не-строковое отбрасывается. */
export const viewJsonStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

export const viewJsonString = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const sortedViewJson = <F, K extends keyof F>(
  keys: ReadonlyArray<K>,
  filters: F,
) => {
  const view = filtersToViewJson(keys, filters) as Record<string, unknown>;
  return Object.keys(view)
    .sort()
    .map((key) => {
      const value = view[key];
      return [key, Array.isArray(value) ? [...value].sort() : value];
    });
};

/**
 * Те же фильтры по ключам белого списка (остальное — страница и т. п. — не
 * в счёт; порядок значений в массиве — тоже).
 */
export const sameFiltersBy = <F, K extends keyof F>(
  keys: ReadonlyArray<K>,
  a: F,
  b: F,
): boolean =>
  JSON.stringify(sortedViewJson(keys, a)) ===
  JSON.stringify(sortedViewJson(keys, b));

/** Готовый вид (пресет): ключ и его фильтры. */
export interface IFilterPreset<F, P extends string = string> {
  key: P;
  filters: F;
}

/** Пресеты списком: ключи → их фильтры (по порядку `keys`). */
export const filterPresets = <F, P extends string>(
  keys: ReadonlyArray<P>,
  filtersOf: (preset: P) => F,
): Array<IFilterPreset<F, P>> =>
  keys.map((key) => ({ key, filters: filtersOf(key) }));

/** Пресет, чьи фильтры совпадают с текущими, — или `null`. */
export const findActivePreset = <F, P extends string>(
  presets: ReadonlyArray<IFilterPreset<F, P>>,
  filters: F,
  same: (a: F, b: F) => boolean,
): IFilterPreset<F, P> | null =>
  presets.find((preset) => same(preset.filters, filters)) ?? null;
